/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Hook for Dynamic Admin Portal Template Resolution (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useState, useEffect, useMemo } from 'react';
import { getAdminDomains } from '../lib/urls';
import {
  getAdminTemplate,
  isValidAdminTemplate,
  type AdminLayoutComponent,
} from '../templates/registry';

export interface ActiveAdminTemplateResult {
  templateId: string;
  LayoutComponent: AdminLayoutComponent;
  isLoading: boolean;
  source: 'query' | 'session' | 'api' | 'default';
}

const SESSION_STORAGE_KEY = 'sophia_admin_preview_template';

/**
 * Resolves the currently active template for Admin Portal.
 *
 * Priority order:
 * 1. URL Query Parameter (?template=<slug>) - Immediate Live Preview
 * 2. Session Storage cache (persists preview while navigating within session)
 * 3. Cloudflare Edge Public API (GET /api/v1/public/templates -> adminPortal.activeTemplate)
 * 4. Fallback default ('default')
 */
export function useActiveAdminTemplate(): ActiveAdminTemplateResult {
  // 1. Initial synchronous check from URL or SessionStorage
  const initialResolution = useMemo<{ templateId: string; source: 'query' | 'session' | 'default' }>(() => {
    if (typeof window === 'undefined') {
      return { templateId: 'default', source: 'default' };
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const queryTemplate = urlParams.get('template');

      if (queryTemplate && isValidAdminTemplate(queryTemplate)) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, queryTemplate.trim().toLowerCase());
        return { templateId: queryTemplate.trim().toLowerCase(), source: 'query' };
      }

      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored && isValidAdminTemplate(stored)) {
        return { templateId: stored.trim().toLowerCase(), source: 'session' };
      }
    } catch {
      // Ignore storage access errors in restricted browser environments
    }

    return { templateId: 'default', source: 'default' };
  }, []);

  const [templateId, setTemplateId] = useState<string>(initialResolution.templateId);
  const [source, setSource] = useState<'query' | 'session' | 'api' | 'default'>(initialResolution.source);
  const [isLoading, setIsLoading] = useState<boolean>(initialResolution.source === 'default');

  // 2. Asynchronously fetch Edge Active Template if no query/session override is active
  useEffect(() => {
    let mounted = true;

    // If already resolved via query parameter or session storage, don't overwrite with API
    if (initialResolution.source === 'query' || initialResolution.source === 'session') {
      setIsLoading(false);
      return;
    }

    const fetchActiveTemplate = async () => {
      try {
        const domains = getAdminDomains();
        const res = await fetch(`${domains.apiOrigin}/api/v1/public/templates`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json = (await res.json()) as any;
        const serverActiveId =
          (typeof json?.data?.adminPortal === 'string' ? json.data.adminPortal : json?.data?.adminPortal?.activeTemplate) ||
          json?.data?.activeTemplates?.adminPortal ||
          json?.data?.admin_portal ||
          json?.data?.adminPortal?.activeTemplateId;

        if (mounted && serverActiveId && typeof serverActiveId === 'string') {
          const normalized = serverActiveId.trim().toLowerCase();
          setTemplateId(normalized);
          setSource('api');
        }
      } catch {
        // Fallback remains 'default'
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchActiveTemplate();

    return () => {
      mounted = false;
    };
  }, [initialResolution]);

  const LayoutComponent = useMemo(() => {
    return getAdminTemplate(templateId);
  }, [templateId]);

  return {
    templateId,
    LayoutComponent,
    isLoading,
    source,
  };
}

